import React, { useEffect, useState } from "react";
import { styled } from "@acko-tech/building-blocks.ui.common";
import { Flex } from "@acko-tech/building-blocks.ui.common";
import { convertToLakh, convertToLakhMin } from "@utils/inrFormatter";
import { setGlobalData } from "@actions/setGlobalData";
import { analyticEvents as triggerAnalyticEvent } from "@actions/analyticEvents";
import { debounce } from "utils/lodash";
import { getRequestpayload } from "@utils/index";

const InputContainer = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid ${(props) => (props.showError ? "#D83D37" : "#e0e0e8")};
  border-radius: 8px;
  padding: 10px;
  width: fit-content;
  background-color: #fff;
  margin-bottom: 3rem;
  ${(props) => props.styles} 
`;

const CurrencySymbol = styled.span`
  color: #5920c5;
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;

  ${(props) => props.styles}
`;

const Input = styled.input`
  border: none;
  outline: none;
  color: #5920c5;
  width: min-content;
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
  box-sizing: content-box;
`;

const Unit = styled.span`
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #5b5675;
  margin-left: 2px;
`;

const ErrorMessage = styled.div`
  position: absolute;
  color: #d83d37;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  margin-top: 0.5rem;
  bottom: 16px;

  ${(props) => props.styles}
`;

interface IdvInputBoxProps {
  selectedIdv: number;
  setSelectedIdv: (value: number) => void;
  minIdv: number;
  maxIdv: number;
  recommendedIdv: number;
  onInputEnd?: (value: number) => void;
  styles?: any;
}

const IdvInputBox = ({
  selectedIdv,
  setSelectedIdv,
  minIdv,
  maxIdv,
  recommendedIdv,
  onInputEnd,
  styles = {}
}: IdvInputBoxProps) => {
  const [val, setVal] = useState<any>(0.0);
  const [showError, setShowError] = useState<boolean>(false);

  const ErrorMessageText = ({ minIdv, maxIdv }) => {
    return (
      <span>{`IDV should be between ₹ ${convertToLakhMin(minIdv)}lakh and ₹ ${convertToLakhMin(maxIdv)}lakh`}</span>
    );
  };

  useEffect(() => {
    const formattedIdv = convertToLakh(selectedIdv);
    setVal(formattedIdv);
  }, [selectedIdv]);

  const { data, journey } = getRequestpayload();

  const debouncedHandleChange = React.useCallback(
    debounce((value: string) => {
      if (!/^\d*\.?\d*$/.test(value)) return;
      const selectedIdvInputValue = Number(value) * 100000;
      if (selectedIdvInputValue > maxIdv || selectedIdvInputValue < minIdv) {
        setGlobalData({
          globalData: {
            idv_error: true
          }
        });
        setShowError(true);
      } else {
        setGlobalData({
          globalData: {
            idv_error: false
          }
        });
        setSelectedIdv(selectedIdvInputValue);
        triggerAnalyticEvent({
          edata: {
            product: data.product,
            action_name: "idv_value_edited",
            action_type: "value_entered",
            initial_idv: selectedIdv,
            recommended_idv: recommendedIdv,
            selected_idv: selectedIdvInputValue
          },
          odata: {
            journey
          },
          event_name: "action"
        });
        setShowError(false);
      }
      setVal(value);
    }, 100),
    [maxIdv, minIdv, recommendedIdv, selectedIdv, setSelectedIdv]
  );

  const handleInputFinish = () => {
    const currentValue = Number(val) * 100000;
    if (!showError && onInputEnd) {
      onInputEnd(currentValue);
    }
  };

  useEffect(() => {
    return () => {
      debouncedHandleChange.cancel();
    };
  }, [debouncedHandleChange]);

  useEffect(() => {
    const IdvInputBox = document.getElementById("idv-input-box");
    const handleKeyUp = ({ key }: KeyboardEvent) => {
      if (key === "Enter") {
        (document.activeElement as HTMLElement)?.blur();
        handleInputFinish();
      }
    };

    IdvInputBox?.addEventListener("keyup", handleKeyUp);
    return () => {
      IdvInputBox?.removeEventListener("keyup", handleKeyUp);
    };
  }, [val, showError]);

  return (
    <Flex flexDirection="column" styles={{ position: "relative" }}>
      <InputContainer showError={showError} styles={styles.inputContainer}>
        <CurrencySymbol style={styles.currencySymbol}>₹</CurrencySymbol>
        <Input
          type="tel"
          value={val}
          size={3}
          onChange={(e) => {
            const value = e.target.value;
            if (!/^\d*\.?\d*$/.test(value) && value !== '') return;
            debouncedHandleChange(value);
          }}
          onBlur={handleInputFinish}
          id="idv-input-box"
          style={{
            ...styles.input,
            width: `${Math.max(3, val.toString().length - 0.7)}ch`,
          }}
        />
        <Unit style={styles.unit}>lakh</Unit>
      </InputContainer>
      {showError && (
        <ErrorMessage style={styles.errorMessage}>
          <ErrorMessageText minIdv={minIdv} maxIdv={maxIdv} />
        </ErrorMessage>
      )}
    </Flex>
  );
};

export default IdvInputBox;
